import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSimpleMode } from '@/contexts/SimpleModeContext';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import {
  ClipboardCheck, Camera, Check, Users, ShieldCheck,
  MessageSquare, Lightbulb, AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface ChecklistItem {
  id: string;
  label: string;
  emoji: string;
  dbField: string;
}

interface FarmerOption {
  id: string;
  full_name: string;
  phone: string | null;
  region: string | null;
  farm_id: string | null;
  country_id: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const BIOSECURITY_CHECKLIST: ChecklistItem[] = [
  { id: 'footbath', label: 'Footbath at entrance', emoji: '🦶', dbField: 'footbath_active' },
  { id: 'visitor_log', label: 'Visitor log maintained', emoji: '📋', dbField: 'visitor_log_maintained' },
  { id: 'wild_bird', label: 'Wild bird netting', emoji: '🦅', dbField: 'wild_bird_access_prevented' },
  { id: 'litter', label: 'Clean litter condition', emoji: '🧹', dbField: 'litter_condition' },
  { id: 'feed_storage', label: 'Feed storage sealed', emoji: '🌾', dbField: 'feed_storage_secure' },
  { id: 'water', label: 'Water system clean', emoji: '💧', dbField: 'water_source_clean' },
  { id: 'dead_bird', label: 'Dead bird removal protocol', emoji: '☠️', dbField: 'dead_bird_disposal_method' },
  { id: 'disinfection', label: 'Disinfection schedule followed', emoji: '🧴', dbField: 'footbath_present' },
  { id: 'ventilation', label: 'Proper ventilation', emoji: '🌬️', dbField: 'ventilation_adequate' },
  { id: 'rodent', label: 'Rodent control measures', emoji: '🐀', dbField: 'rodent_control_active' },
];

const RECOMMENDATION_CHIPS: { key: string; label: string }[] = [
  { key: 'ventilation', label: 'Improve ventilation' },
  { key: 'replaceLitter', label: 'Replace litter' },
  { key: 'fixNetting', label: 'Fix netting' },
  { key: 'adjustFeed', label: 'Adjust feed' },
  { key: 'scheduleVaccination', label: 'Schedule vaccination' },
];

// ============================================================================
// Component
// ============================================================================

export const SupervisorVisitScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, getSessionToken } = useAuth();
  const { isSimpleMode } = useSimpleMode();

  const [selectedFarmerId, setSelectedFarmerId] = useState<string>('');
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [observations, setObservations] = useState('');
  const [selectedRecs, setSelectedRecs] = useState<string[]>([]);
  const [customRec, setCustomRec] = useState('');
  const [photosTaken, setPhotosTaken] = useState(0);

  const userId = getCurrentUserId();
  const client = getAuthenticatedClient();

  // ── Verify field officer role ──────────────────────────
  const { data: memberRole, isLoading: isRoleLoading } = useQuery({
    queryKey: ['member-role', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await client
        .from('organization_members')
        .select('member_role, assigned_region, organization_id')
        .eq('user_id', userId)
        .in('member_role', ['field_officer', 'manager', 'admin', 'owner', 'integrator_admin'])
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // ── Fetch farmers ──────────────────────────────────────
  const { data: farmers = [], isLoading: isFarmersLoading } = useQuery({
    queryKey: ['region-farmers', memberRole?.assigned_region],
    queryFn: async () => {
      if (!memberRole) return [];
      let query = client
        .from('profiles')
        .select('id, full_name, phone, region, country_id')
        .eq('user_type', 'farmer')
        .eq('account_status', 'active')
        .is('deleted_at', null);

      if (memberRole.assigned_region) {
        query = query.eq('region', memberRole.assigned_region);
      }

      const { data, error } = await query.order('full_name').limit(100);
      if (error) throw error;

      const farmerIds = (data || []).map((f: any) => f.id);
      let farmMap: Record<string, string> = {};

      if (farmerIds.length > 0) {
        const { data: farms } = await client
          .from('farms')
          .select('id, user_id')
          .in('user_id', farmerIds)
          .eq('is_active', true)
          .is('deleted_at', null);

        if (farms) {
          farms.forEach((f: any) => {
            if (!farmMap[f.user_id]) farmMap[f.user_id] = f.id;
          });
        }
      }

      return (data || []).map((f: any) => ({
        id: f.id,
        full_name: f.full_name || t("common.unknown", { defaultValue: "Unknown" }),
        phone: f.phone,
        region: f.region,
        farm_id: farmMap[f.id] || null,
        country_id: f.country_id,
      })) as FarmerOption[];
    },
    enabled: !!memberRole,
  });

  // ── Submit mutation ────────────────────────────────────
  const submitMutation = useMutation({
    mutationFn: async () => {
      const farmer = farmers.find((f) => f.id === selectedFarmerId);
      if (!farmer || !farmer.farm_id || !userId) throw new Error('Missing farmer or farm data');

      const totalItems = BIOSECURITY_CHECKLIST.length;
      const passedItems = Object.values(checks).filter(Boolean).length;
      const scorePct = Math.round((passedItems / totalItems) * 100);
      const riskLevel = scorePct >= 80 ? 'low' : scorePct >= 50 ? 'medium' : 'high';
      const allRecs = [...selectedRecs, customRec].filter(Boolean).join('; ');

      const { error } = await client
        .from('biosecurity_assessments')
        .insert({
          farm_id: farmer.farm_id!,
          user_id: farmer.id,
          assessed_by: userId,
          country_id: farmer.country_id!,
          assessment_date: new Date().toISOString().split('T')[0],
          footbath_active: checks['footbath'] ?? false,
          visitor_log_maintained: checks['visitor_log'] ?? false,
          wild_bird_access_prevented: checks['wild_bird'] ?? false,
          litter_condition: checks['litter'] ? 'clean' : 'needs_attention',
          feed_storage_secure: checks['feed_storage'] ?? false,
          water_source_clean: checks['water'] ?? false,
          dead_bird_disposal_method: checks['dead_bird'] ? 'proper_protocol' : 'needs_improvement',
          footbath_present: checks['disinfection'] ?? false,
          ventilation_adequate: checks['ventilation'] ?? false,
          rodent_control_active: checks['rodent'] ?? false,
          total_score: passedItems,
          max_possible_score: totalItems,
          score_pct: scorePct,
          risk_level: riskLevel,
          notes: observations || null,
          recommendations: allRecs || null,
        });
      if (error) throw error;

      await client.from('activities').insert({
        user_id: userId,
        activity_type: 'biosecurity_check' as any,
        activity_date: new Date().toISOString().split('T')[0],
        notes: `${t("supervisor.visit.farmVisitPrefix", { defaultValue: "Farm visit" })}: ${farmer.full_name} — Score: ${scorePct}%`,
        points_earned: 20,
        farm_id: farmer.farm_id,
      });
    },
    onSuccess: () => {
      toast({ title: `✅ ${t("supervisor.visit.recorded", { defaultValue: "Visit recorded" })}`, description: t("supervisor.visit.recordedDesc", { defaultValue: "Biosecurity assessment saved successfully." }) });
      navigate(-1);
    },
    onError: (err: any) => {
      toast({ title: t("supervisor.visit.saveFailed", { defaultValue: "Error saving visit" }), description: err.message, variant: 'destructive' });
    },
  });

  // ── Handlers ───────────────────────────────────────────
  const toggleCheck = (id: string) => setChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleRec = (rec: string) => setSelectedRecs((prev) => prev.includes(rec) ? prev.filter((r) => r !== rec) : [...prev, rec]);
  const handleTakePhoto = () => { navigate('/camera?category=shed_interior'); setPhotosTaken((p) => p + 1); };

  const selectedFarmer = farmers.find((f) => f.id === selectedFarmerId);
  const completedChecks = Object.values(checks).filter(Boolean).length;
  const canSubmit = selectedFarmerId && completedChecks > 0 && !submitMutation.isPending;
  const scorePct = Math.round((completedChecks / BIOSECURITY_CHECKLIST.length) * 100);

  // ── Access check ───────────────────────────────────────
  if (isRoleLoading) {
    return (
      <Screen topBarProps={{ title: t("supervisor.visit.title", { defaultValue: "Farm Visit" }), showBack: true }} showBottomNav={false}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Screen>
    );
  }

  if (!memberRole) {
    return (
      <Screen topBarProps={{ title: t("supervisor.visit.accessDenied", { defaultValue: "Access Denied" }), showBack: true }} showBottomNav={false}>
        <div className="p-6 text-center space-y-4">
          <AlertTriangle className="w-16 h-16 text-warning mx-auto" />
          <h2 className="text-xl font-bold text-foreground">{t("supervisor.visit.fieldOfficersOnly", { defaultValue: "Field Officers Only" })}</h2>
          <p className="text-muted-foreground">{t("supervisor.visit.accessDeniedDesc", { defaultValue: "This screen is only accessible to field officers and supervisors." })}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>{t("common.back")}</Button>
        </div>
      </Screen>
    );
  }

  // ── Render ─────────────────────────────────────────────
  return (
    <Screen
      topBarProps={{ title: isSimpleMode ? '🏠 Visit' : t("supervisor.visit.title", { defaultValue: "Farm Visit" }), showBack: true, onBack: () => navigate(-1) }}
      showBottomNav={false}
    >
      <div className="p-4 space-y-5 pb-32">
        {/* Region Badge */}
        {memberRole.assigned_region && (
          <Badge variant="secondary" className={cn("text-sm", isSimpleMode && "text-base px-4 py-2")}>
            📍 {memberRole.assigned_region}
          </Badge>
        )}

        {/* 1. Select Farmer */}
        <Card className="p-4 space-y-3">
          <div className={cn("flex items-center gap-2 text-foreground font-semibold", isSimpleMode && "text-lg")}>
            {isSimpleMode ? <span className="text-2xl">👨‍🌾</span> : <Users className="w-5 h-5 text-primary" />}
            {!isSimpleMode && t("supervisor.visit.selectFarmer", { defaultValue: "Select Farmer" })}
          </div>
          <Select value={selectedFarmerId} onValueChange={setSelectedFarmerId}>
            <SelectTrigger className={cn("text-base", isSimpleMode ? "h-16 text-lg" : "h-14")}>
              <SelectValue placeholder={isFarmersLoading ? t("common.loading") : '👨‍🌾'} />
            </SelectTrigger>
            <SelectContent>
              {farmers.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  <div className="flex flex-col">
                    <span className="font-medium">{f.full_name}</span>
                    {f.phone && <span className="text-xs text-muted-foreground">{f.phone}</span>}
                  </div>
                </SelectItem>
              ))}
              {farmers.length === 0 && !isFarmersLoading && (
                <div className="p-3 text-sm text-muted-foreground text-center">{t("supervisor.noFarmers")}</div>
              )}
            </SelectContent>
          </Select>

          {selectedFarmer && !selectedFarmer.farm_id && (
            <p className="text-sm text-warning flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> {t("supervisor.visit.noRegisteredFarm", { defaultValue: "No registered farm" })}
            </p>
          )}
        </Card>

        {/* 2. Biosecurity Checklist */}
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className={cn("flex items-center gap-2 text-foreground font-semibold", isSimpleMode && "text-lg")}>
              {isSimpleMode ? <span className="text-2xl">🛡️</span> : <ShieldCheck className="w-5 h-5 text-primary" />}
              {!isSimpleMode && t("supervisor.visit.biosecurityChecklist", { defaultValue: "Biosecurity Checklist" })}
            </div>
            <Badge variant={completedChecks >= 8 ? 'default' : 'secondary'} className={cn(isSimpleMode && "text-lg px-3 py-1")}>
              {isSimpleMode ? `⭐ ${scorePct}%` : `${completedChecks}/${BIOSECURITY_CHECKLIST.length}`}
            </Badge>
          </div>

          <div className="space-y-2">
            {BIOSECURITY_CHECKLIST.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'flex items-center justify-between rounded-xl transition-colors',
                  isSimpleMode ? 'p-4 min-h-[56px]' : 'p-3',
                  checks[item.id] ? 'bg-success/10 border border-success/30' : 'bg-muted/50'
                )}
              >
                <div className="flex items-center gap-3">
                  <span className={cn(isSimpleMode ? "text-3xl" : "text-xl")}>{item.emoji}</span>
                  {!isSimpleMode && <span className="text-sm font-medium text-foreground">{t(`supervisor.visit.checklist.${item.id}`, { defaultValue: item.label })}</span>}
                </div>
                <Switch
                  checked={checks[item.id] || false}
                  onCheckedChange={() => toggleCheck(item.id)}
                  className={cn(isSimpleMode && "scale-125")}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* 3. Observations */}
        {!isSimpleMode && (
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-foreground font-semibold">
              <MessageSquare className="w-5 h-5 text-primary" />
              {t("supervisor.visit.observations", { defaultValue: "Observations" })}
            </div>
            <Textarea
              placeholder={t("supervisor.visit.observationsPlaceholder", { defaultValue: "Describe what you observed..." })}
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              className="min-h-[100px]"
            />
          </Card>
        )}

        {/* 4. Photos */}
        <Card className="p-4 space-y-3">
          <Button
            variant="outline"
            className={cn("w-full gap-2", isSimpleMode ? "h-16 text-xl" : "h-14 text-base")}
            onClick={handleTakePhoto}
          >
            <span className={cn(isSimpleMode ? "text-3xl" : "text-xl")}>📸</span>
            {!isSimpleMode && t("camera.takePhoto")}
            {photosTaken > 0 && (
              <Badge variant="default" className="ml-2">{photosTaken}</Badge>
            )}
          </Button>
        </Card>

        {/* 5. Recommendations */}
        {!isSimpleMode && (
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-foreground font-semibold">
              <Lightbulb className="w-5 h-5 text-warning" />
              {t("supervisor.visit.recommendationsTitle", { defaultValue: "Recommendations" })}
            </div>
            <div className="flex flex-wrap gap-2">
              {RECOMMENDATION_CHIPS.map((rec) => (
                <button
                  key={rec.key}
                  onClick={() => toggleRec(rec.label)}
                  className={cn(
                    'px-3 py-2 rounded-full text-sm font-medium transition-all',
                    selectedRecs.includes(rec.label)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                >
                  {t(`supervisor.visit.recommendations.${rec.key}`, { defaultValue: rec.label })}
                </button>
              ))}
            </div>
            <Textarea
              placeholder={t("supervisor.visit.additionalRecommendations", { defaultValue: "Additional recommendations..." })}
              value={customRec}
              onChange={(e) => setCustomRec(e.target.value)}
              className="min-h-[60px]"
            />
          </Card>
        )}
      </div>

      {/* Sticky Submit */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-border">
        <Button
          className={cn("w-full gap-2", isSimpleMode ? "h-20 text-2xl" : "h-16 text-lg")}
          onClick={() => submitMutation.mutate()}
          disabled={!canSubmit}
        >
          {submitMutation.isPending ? (
            <><span className="animate-spin">⏳</span> {!isSimpleMode && t("supervisor.visit.saving", { defaultValue: "Saving visit..." })}</>
          ) : (
            <>
              {isSimpleMode ? '✅' : <ClipboardCheck className="w-6 h-6" />}
              {!isSimpleMode && t("supervisor.visit.submitReport", { defaultValue: "Submit Visit Report" })}
            </>
          )}
        </Button>
      </div>
    </Screen>
  );
};

export default SupervisorVisitScreen;
